/* eslint-disable sonarjs/no-commented-code */
import useCodeNavigate from "components/router/useCodeNavigate";
import { CodeSystem } from "context/elasticsearch/types";
import { Fragment, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { codesystemsEndpointName, getApiEndpoint } from "utils";

import { HierarchyResponse } from "./interface";
import Node from "./Node";
import { HierarchyWrapper } from "./style";

type HierarchyProps = {
  codeSystem: CodeSystem;
};

const Hierarchy = ({ codeSystem }: HierarchyProps) => {
  const navigate = useCodeNavigate();
  //const { t } = useTranslation()

  const hierarchyScrollRef = useRef<Record<string, HTMLDivElement | null>>({});
  const { codeValue: code } = useParams();

  // fetchError was unused (commented out in JSX, so commented out everything that touches this)
  //const [fetchError, setFetchError] = useState<string>('')

  const [expandedNodes, setExpandedNodes] = useState<string[]>([]);

  const [hierarchy, setHierarchy] = useState<HierarchyResponse>();

  const pendingScrollCode = useRef<string | null>(null);

  // Fetch methods defined in component in case we need fetchError later
  const fetchHierarchy = async (
    codeSystemValue: string,
    codeValue: string
  ): Promise<HierarchyResponse> => {
    if (codeValue === undefined || codeValue === "") codeValue = "root";

    const res = await fetch(
      getApiEndpoint(codeSystemValue, codeValue, {
        useHierarchy: true,
        includeInactive: codeSystemValue === CodeSystem.Norpat
      })
    );

    // Non-404 errors
    // if (!res.ok && res.status !== 404) {
    // 	setFetchError(res.statusText)
    // }

    return await res.json();
  };

  const fetchChildren = async (
    codeSystemValue: string,
    codeValue: string
  ): Promise<HierarchyResponse> => {
    const res = await fetch(
      getApiEndpoint(codeSystemValue as string, codeValue, {
        useChildren: true,
        includeInactive: codeSystemValue === codesystemsEndpointName.norpat
      })
    );

    // Non-404 errors
    // if (!res.ok && res.status !== 404) {
    // 	setFetchError(res.statusText)
    // }

    return await res.json();
  };

  // Merge two hierarchies recursively (recursive tree merge)
  const mergeHierarchy = (
    currentHierarchy: HierarchyResponse,
    newHierarchy: HierarchyResponse
  ) => {
    // Ensure same node for both hierarchies
    if (currentHierarchy.codeValue !== newHierarchy.codeValue) {
      throw new Error("mergeHierarchy(): Cannot merge two different nodes");
    }

    // No current children, use new children even if empty
    if (
      currentHierarchy.children === undefined ||
      currentHierarchy.children.length === 0
    ) {
      currentHierarchy.children = newHierarchy.children;
      return currentHierarchy;
    }
    // No incoming children, keep current children
    else if (
      newHierarchy.children === undefined ||
      newHierarchy.children.length === 0
    ) {
      return currentHierarchy;
    }

    // Both have children, must be merged
    // Create a map of the current children
    const currentChildrenMap = new Map<string, HierarchyResponse>();
    for (const child of currentHierarchy.children) {
      currentChildrenMap.set(child.codeValue, child);
    }

    // Merge the new children into the current children
    for (const child of newHierarchy.children) {
      const currentChild = currentChildrenMap.get(child.codeValue);
      if (currentChild) {
        // Merge the current child with the new child
        mergeHierarchy(currentChild, child);
      } else {
        // Should never happen
        throw new Error(
          "mergeHierarchy(): Child not found in current hierarchy"
        );
      }
    }

    return currentHierarchy;
  };

  // Recursive depth-first search for a node in the hierarchy
  const findInHierarchy = (
    node: HierarchyResponse,
    codeValue: string
  ): HierarchyResponse | undefined => {
    if (node.codeValue === codeValue) {
      return node;
    }

    if (node.children) {
      for (const child of node.children) {
        const found = findInHierarchy(child, codeValue);
        if (found) {
          return found;
        }
      }
    }

    return undefined;
  };

  const flipExpandedStateForItem = (theItem: HierarchyResponse) => {
    setExpandedNodes((prevExpandedNodes) => {
      if (prevExpandedNodes.includes(theItem.codeValue)) {
        // If the node is already expanded, remove it from expandedNodes
        return prevExpandedNodes.filter(
          (codeValue) => codeValue !== theItem.codeValue
        );
      }

      // If the node is not expanded, add it to expandedNodes
      const newExpandedNodes = [...prevExpandedNodes, theItem.codeValue];
      return newExpandedNodes;
    });
  };

  const handleExpand = (item: HierarchyResponse) => () => {
    if (item.isLeafNode === false && item.children === undefined) {
      fetchChildren(codeSystem as string, item.codeValue).then(
        (fetchedCode) => {
          item.children = fetchedCode.children;
          item.path = fetchedCode.path;

          // Force React to see the change by creating a new object reference
          // eslint-disable-next-line sonarjs/no-nested-functions
          setHierarchy((prev) => (prev ? { ...prev } : prev));

          flipExpandedStateForItem(item);
        }
      );
    } else {
      flipExpandedStateForItem(item);
    }
  };

  // Fetch and expand hierarchy when codeSystem or code changes
  useEffect(() => {
    if (!codeSystem) return;

    let cancelled = false;

    const loadAndExpand = async () => {
      // Fetch hierarchy for current code, use root if undefined
      const codeToFetch = code ?? "root";
      const newHierarchy = await fetchHierarchy(codeSystem, codeToFetch);
      if (cancelled) return;

      // Clear expanded nodes if no code selected
      // Will return hierarchy root, but previously loaded nodes will still exist in state
      if (!code) {
        setHierarchy((prev) =>
          prev ? { ...mergeHierarchy(prev, newHierarchy) } : { ...newHierarchy }
        );
        setExpandedNodes([]);
        return;
      }

      // Find node in hierarchy structure
      const node = findInHierarchy(newHierarchy, code);
      if (!node) {
        // eslint-disable-next-line no-console
        console.log("HierarchyError: node not found");
        return;
      }

      // Fetch children/path if missing BEFORE merging into state.
      // This ensures newHierarchy is fully populated so the merge
      // propagates children to the state tree correctly.
      if (!node.path || (!node.isLeafNode && !node.children)) {
        const fetched = await fetchChildren(codeSystem, code);
        if (cancelled) return;

        node.children = fetched.children;
        node.path = fetched.path;
      }

      // Merge fully-populated hierarchy into state
      setHierarchy((prev) =>
        prev ? { ...mergeHierarchy(prev, newHierarchy) } : { ...newHierarchy }
      );

      // Expand path
      setExpandedNodes((prev) => [...new Set([...prev, ...node.path])]);

      // Set pending scroll target
      pendingScrollCode.current = code;
    };

    loadAndExpand();

    // Cleanup function to cancel async operations if triggered again before completion
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codeSystem, code]);

  // Scroll only if we have a pending scroll target set by the fetch/expand effect
  useEffect(() => {
    if (!pendingScrollCode.current) return;

    const hierarchyElement =
      hierarchyScrollRef.current[pendingScrollCode.current];
    if (hierarchyElement) {
      hierarchyElement.scrollIntoView({ behavior: "smooth", block: "start" });
      pendingScrollCode.current = null; // Clear after scrolling
    }
  }, [expandedNodes]);

  const handleNavigate = (item: HierarchyResponse) => () => {
    // If selected item is active and collapsed, expand it
    if (
      item.codeValue === code && // item is active
      !expandedNodes.includes(item.codeValue) && // item is collapsed
      item.isLeafNode === false && // item has children
      item.children !== undefined // item's children are loaded
    ) {
      return flipExpandedStateForItem(item);
    }

    navigate.toCode(codeSystem, item.codeValue);
  };

  // Render the entire hierarchy tree recursively
  const renderItems = (items: HierarchyResponse[], level = 0) => {
    return (
      items &&
      items.map((item) => {
        return (
          <Fragment key={item.codeValue}>
            <Node
              item={item}
              level={level}
              isActive={code === item.codeValue}
              isExpanded={expandedNodes.includes(item.codeValue)}
              handleNavigate={handleNavigate(item)}
              handleExpand={handleExpand(item)}
              innerRef={(ref: HTMLDivElement | null) => {
                hierarchyScrollRef.current[item.codeValue] = ref;
              }}
            >
              {expandedNodes.includes(item.codeValue) &&
                renderItems(item.children, level + 1)}
            </Node>
          </Fragment>
        );
      })
    );
  };

  return (
    <>
      {/* TODO: consider making hierarchy a recursive element to make renderItems redundant and re-implement error and loading */}
      <HierarchyWrapper id={`${codeSystem}-hierarchy-tree`}>
        {/* {isLoading && <Loading />}
				{isError && <ErrorMessage errorResponse={fetchError} />} */}
        {renderItems(hierarchy?.children as HierarchyResponse[])}
      </HierarchyWrapper>
    </>
  );
};

export default Hierarchy;
